import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore.js';
import { LoginPage } from './pages/LoginPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { EditorPage } from './pages/EditorPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { EmailFooterPage } from './pages/EmailFooterPage.js';
import { AppShell } from './components/AppShell.js';
import { EmailsPage } from './pages/EmailsPage.js';
import { EmailEditorPage } from './pages/EmailEditorPage.js';
import { EmailSendersPage } from './pages/EmailSendersPage.js';
import { SenderViewPage } from './pages/SenderViewPage.js';
import { EmailListsPage } from './pages/EmailListsPage.js';
import { EmailSendPage } from './pages/EmailSendPage.js';
import { EmailHistoryPage } from './pages/EmailHistoryPage.js';
import { UnsubscribesPage } from './pages/UnsubscribesPage.js';
import { ComponentsPage } from './pages/ComponentsPage.js';
import { ComponentEditorPage } from './pages/ComponentEditorPage.js';
import { BillingPage } from './pages/BillingPage.js';
import { CreditsPage } from './pages/CreditsPage.js';
import { PlansPage } from './pages/PlansPage.js';
import { InvoicesPage } from './pages/InvoicesPage.js';
import { BillingReturnPage } from './pages/BillingReturnPage.js';
import { MembersPage } from './pages/MembersPage.js';
import { AcceptInvitePage } from './pages/AcceptInvitePage.js';
import { AdminPage } from './pages/AdminPage.js';
import { InstanceSetupPage } from './pages/InstanceSetupPage.js';
import { WorkspaceSetupPage } from './pages/WorkspaceSetupPage.js';
import { ApiAccessPage } from './pages/ApiAccessPage.js';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        {/*
          First-run instance setup. Public by necessity — it creates the
          account that creates every other account — and it redirects itself
          to /login once a platform admin exists.
        */}
        <Route path="/setup" element={<InstanceSetupPage />} />
        {/*
          Workspace onboarding sits OUTSIDE AppShell on purpose: it is a
          full-page task, and AppShell's own rule ("a workspace that needs
          setup redirects to /workspace/setup") would otherwise fight the
          wizard for the route it redirects to.
        */}
        <Route
          path="/workspace/setup"
          element={
            <PrivateRoute>
              <WorkspaceSetupPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <AppShell />
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to="/posts" replace />} />
          <Route path="posts" element={<DashboardPage />} />
          <Route path="posts/:id/edit" element={<EditorPage />} />
          <Route path="emails" element={<EmailsPage />} />
          <Route path="emails/send" element={<EmailSendPage />} />
          <Route path="emails/senders" element={<EmailSendersPage />} />
          {/* One sender in full: metrics, domain verification, mail health. */}
          <Route path="emails/senders/:id" element={<SenderViewPage />} />
          <Route path="emails/lists" element={<EmailListsPage />} />
          <Route path="emails/history" element={<EmailHistoryPage />} />
          <Route path="emails/unsubscribes" element={<UnsubscribesPage />} />
          <Route path="emails/:id/edit" element={<EmailEditorPage />} />
          <Route path="emails/:id/footer" element={<EmailFooterPage scope="email" />} />
          <Route path="components" element={<ComponentsPage />} />
          <Route path="components/new" element={<ComponentEditorPage />} />
          <Route path="components/:id/edit" element={<ComponentEditorPage />} />
          <Route path="billing" element={<BillingPage />}>
            <Route index element={<Navigate to="credits" replace />} />
            <Route path="credits" element={<CreditsPage />} />
            <Route path="plans" element={<PlansPage />} />
            <Route path="invoices" element={<InvoicesPage />} />
          </Route>
          <Route path="billing/return" element={<BillingReturnPage />} />
          <Route path="workspace/members" element={<MembersPage />} />
          {/* Platform administration — the server 403s anyone without the flag. */}
          <Route path="admin" element={<AdminPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="settings/email-footer" element={<EmailFooterPage />} />
          {/* Workspace API credentials — owner/admin only (server-enforced). */}
          <Route path="settings/api" element={<ApiAccessPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
